import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

import { fadeInEnter } from '@libs/src/animations/animations';
import {
  Account,
  AccountGroupMembership,
} from '@libs/src/models/account.model';
import {
  Group,
  GroupCustomSegment,
  GroupHiddenCustomSegment,
} from '@libs/src/models/group.model';
import { MatchConfig } from '@libs/src/models/match.interfaces';
import { getSegmentSelectionNodes } from '@libs/src/segment-selections/segment-selections.helper';
import { getMembership } from '@main-client/src/app/account/account.helpers';
import { CustomSegmentsSelections } from '@main-client/src/app/group-intro-selections-form/custom-segment-selections';
import { MatchSelections } from '@main-client/src/app/group-intro-selections-form/match-selections';
import { RoleSelections } from '@main-client/src/app/group-intro-selections-form/role-selections';
import {
  buildSelectableCustomSegmentsMap,
  buildSelectableRolesMap,
  SelectableCustomSegmentsMap,
  SelectableOptionMap,
} from '@main-client/src/app/group-intro-selections/group-intro-selections.helper';
import {
  buildCustomSegmentSelectionsMap,
  buildRoleSelectionsMap,
} from '@main-client/src/app/group-intro-selections/group-intro-selections.helper';

import castArray from 'lodash-es/castArray';
import get from 'lodash-es/get';
import isEmpty from 'lodash-es/isEmpty';
import keys from 'lodash-es/keys';
import pick from 'lodash-es/pick';
import reduce from 'lodash-es/reduce';
import set from 'lodash-es/set';
import some from 'lodash-es/some';
import uniq from 'lodash-es/uniq';
import values from 'lodash-es/values';

interface AllOptionsSelected {
  roles: boolean;

  [segmentId: string]: boolean;
}

interface UserSegments {
  [customSegmentId: string]: string | string[];
}

@Component({
  animations: [fadeInEnter],
  selector: 'app-group-intro-selections-form',
  templateUrl: './group-intro-selections-form.component.html',
})
export class GroupIntroSelectionsFormComponent implements OnInit {
  @Input() group: Group;
  @Input() matchConfig: MatchConfig;
  @Input() user: Account;
  @Input() showHiddenCustomSegments = false;
  @Output() selectionChange = new EventEmitter<MatchSelections>();
  userRole: string;
  userCustomSegments: UserSegments;
  roleSelections: RoleSelections = {};
  customSegmentSelections: CustomSegmentsSelections = {};
  hiddenCustomSegmentSelections: CustomSegmentsSelections = {};
  selectableRoles: SelectableOptionMap;
  selectableCustomSegments: SelectableCustomSegmentsMap;
  selectableHiddenCustomSegments: SelectableCustomSegmentsMap;
  allOptionsSelected: AllOptionsSelected = { roles: false };
  customSegmentsToShow: GroupCustomSegment[];
  hiddenCustomSegmentsToShow: GroupHiddenCustomSegment[];

  ngOnInit() {
    const membership =
      getMembership(this.user.groups, this.group._id) ||
      getMembership(this.user.pending_groups, this.group._id);
    this.userRole = membership?.role;
    this.userCustomSegments = this.getUserSegments(
      membership,
      this.group,
      this.showHiddenCustomSegments,
    );
    const isIntroductionsEnabled = this.group.features_enabled?.introductions;
    const accountMatchExclusions = membership?.match_exclusions ?? [];
    const matchSegments = this.matchConfig?.match_segments;
    const customSegmentConfigs = this.matchConfig?.custom_segment_configs;
    this.selectableRoles = buildSelectableRolesMap(
      this.group.roles,
      isIntroductionsEnabled
        ? this.matchConfig.match_segment_level
        : 'preferred',
      isIntroductionsEnabled ? matchSegments : [],
    );
    this.selectableCustomSegments = buildSelectableCustomSegmentsMap(
      this.group.custom_segments,
      isIntroductionsEnabled ? customSegmentConfigs : [],
    );
    const groupRoles = keys(this.selectableRoles).map((name: string) => ({
      name,
    }));
    this.roleSelections = buildRoleSelectionsMap(
      groupRoles,
      accountMatchExclusions,
    );
    const groupCustomSegments = this.buildCustomSegmentsFromSelectableSegments(
      this.selectableCustomSegments,
      this.userCustomSegments,
    );
    this.customSegmentSelections = buildCustomSegmentSelectionsMap(
      groupCustomSegments,
      accountMatchExclusions,
    );
    this.customSegmentsToShow =
      this.getCustomSegmentsSelectableForIntroductionGoals(
        this.group.custom_segments,
      );
    if (this.showHiddenCustomSegments) {
      const hiddenCustomSegmentConfigs =
        this.matchConfig?.hidden_custom_segment_configs;
      this.selectableHiddenCustomSegments = buildSelectableCustomSegmentsMap(
        this.group.hidden_custom_segments,
        isIntroductionsEnabled ? hiddenCustomSegmentConfigs : [],
      );
      this.hiddenCustomSegmentsToShow =
        this.getCustomSegmentsSelectableForIntroductionGoals(
          this.group.hidden_custom_segments,
        ) as GroupHiddenCustomSegment[];
      const groupHiddenCustomSegments =
        this.buildCustomSegmentsFromSelectableSegments(
          this.selectableHiddenCustomSegments,
          this.userCustomSegments,
        );
      this.hiddenCustomSegmentSelections = buildCustomSegmentSelectionsMap(
        groupHiddenCustomSegments,
        accountMatchExclusions,
      );
    }
    this.calculateAllOptionsSelected(
      this.roleSelections,
      this.customSegmentSelections,
      this.hiddenCustomSegmentSelections,
    );
    this.emitChanges(
      this.roleSelections,
      this.customSegmentSelections,
      this.hiddenCustomSegmentSelections,
    );
  }

  toggleRoleSelection(roleSelections: RoleSelections, roleName: string) {
    const currentValue = get(roleSelections, roleName);
    set(roleSelections, roleName, !currentValue);
    this.calculateAllOptionsSelected(
      this.roleSelections,
      this.customSegmentSelections,
      this.hiddenCustomSegmentSelections,
    );
  }

  toggleCustomSegmentSelection(
    customSegmentSelections: CustomSegmentsSelections,
    customSegmentId: string,
    nodeName: string,
  ) {
    const currentValue = get(customSegmentSelections, [
      customSegmentId,
      nodeName,
    ]);
    set(customSegmentSelections, [customSegmentId, nodeName], !currentValue);
    this.calculateAllOptionsSelected(
      this.roleSelections,
      this.customSegmentSelections,
      this.hiddenCustomSegmentSelections,
    );
  }

  emitChanges(
    roleSections?: RoleSelections,
    customSegmentSelections?: CustomSegmentsSelections,
    hiddenCustomSegmentSelections?: CustomSegmentsSelections,
  ) {
    this.selectionChange.emit({
      customSegments: customSegmentSelections,
      hiddenCustomSegments: hiddenCustomSegmentSelections,
      roles: roleSections,
    });
  }

  isRoleSelectable(role: string) {
    return get(this.selectableRoles, [this.userRole, role], false);
  }

  isNodeSelectable(customSegmentId: string, node: string) {
    const selectableSegments = {
      ...this.selectableCustomSegments,
      ...(this.showHiddenCustomSegments && this.selectableHiddenCustomSegments),
    };
    return some(
      values(
        pick(
          selectableSegments[customSegmentId],
          castArray(this.userCustomSegments[customSegmentId]),
        ),
      ),
      (selectableCustomSegment) => get(selectableCustomSegment, node, false),
    );
  }

  private calculateAllOptionsSelected(
    roleSelections: RoleSelections = {},
    customSegmentSelections: CustomSegmentsSelections = {},
    hiddenCustomSegmentSelections: CustomSegmentsSelections = {},
  ) {
    const segmentSelections = {
      ...customSegmentSelections,
      ...(this.showHiddenCustomSegments && hiddenCustomSegmentSelections),
    };
    this.allOptionsSelected = {
      roles: this.isEveryOptionSelected(roleSelections),
      ...Object.keys(segmentSelections).reduce(
        (result, segmentId) => ({
          ...result,
          [segmentId]: this.isEveryOptionSelected(segmentSelections[segmentId]),
        }),
        {},
      ),
    };
  }

  isEveryOptionSelected(options: { [name: string]: boolean }) {
    return Object.values(options).every(Boolean);
  }

  getCustomSegmentsSelectableForIntroductionGoals(
    customSegments: (GroupCustomSegment | GroupHiddenCustomSegment)[] = [],
  ) {
    return customSegments.filter(
      ({ is_selectable_for_user_introduction_goals, nodes }) =>
        is_selectable_for_user_introduction_goals && !isEmpty(nodes),
    );
  }

  buildCustomSegmentsFromSelectableSegments(
    selectableSegments: SelectableCustomSegmentsMap,
    userSegments: UserSegments = {},
  ): (GroupCustomSegment | GroupHiddenCustomSegment)[] {
    return reduce(
      selectableSegments,
      (
        result: (GroupCustomSegment | GroupHiddenCustomSegment)[],
        nodesToMatchWithMap: SelectableOptionMap,
        customSegmentId: string,
      ) => {
        const nodesToMatchWithMapPickedByUser = pick(
          nodesToMatchWithMap,
          castArray(userSegments[customSegmentId]),
        );
        const allNodesToMatchWith = Object.values(
          nodesToMatchWithMapPickedByUser,
        ).flatMap((matchWithMap: { [node: string]: boolean }) =>
          Object.keys(matchWithMap),
        );
        const allUniqueNodesToMatchWith = uniq(allNodesToMatchWith);
        return [
          ...result,
          {
            _id: customSegmentId,
            nodes: allUniqueNodesToMatchWith.map((node) => ({ name: node })),
          },
        ];
      },
      [],
    );
  }

  getUserSegments(
    membership: AccountGroupMembership,
    group: Group,
    showHiddenCustomSegments = false,
  ): UserSegments {
    const segmentSelections = membership.segment_selections || [];
    const hiddenSegmentSelections =
      (showHiddenCustomSegments && membership.hidden_segment_selections) || [];
    const customSegments = group.custom_segments || [];
    const hiddenCustomSegments =
      (showHiddenCustomSegments && group.hidden_custom_segments) || [];
    return segmentSelections
      .concat(hiddenSegmentSelections)
      .reduce((result, segment) => {
        return {
          ...result,
          [segment.custom_segment]: getSegmentSelectionNodes(
            customSegments.concat(hiddenCustomSegments),
            segment,
          ),
        };
      }, {} as any);
  }
}
